import {
  createEmptyCard,
  fsrs,
  generatorParameters,
  GenSeedStrategyWithCardId,
  StrategyMode,
  type Card,
  type FSRS,
} from 'ts-fsrs';

/**
 * FSRS-6 scheduler configuration — the numbers are locked in FLASHCARDS.md:
 *
 * - request_retention 0.9 — the FSRS/Anki default; 85–90% is the language-
 *   learning sweet spot (lower feels demoralizing, higher ~doubles workload).
 * - maximum_interval 180 days — young product, content still moving; not the
 *   library's 100-year default.
 * - fuzz on, seeded per card_key — spreads due dates so sessions don't clump,
 *   while previews stay deterministic for a given card.
 * - learning_steps 1m/10m — Again lands on the 1m step (replayed in-session
 *   via the learn-ahead window); Good on a new card lands on the 10m step,
 *   which is due later the same day (next visit), preserving real spacing.
 * - relearning_steps 1m (not Anki's 10m): a failed review card must be
 *   re-seen before the session ends — retrieval right after failure — and
 *   only the 1m step fits inside the session's learn-ahead window.
 */
export const FSRS_PARAMS = generatorParameters({
  request_retention: 0.9,
  maximum_interval: 180,
  enable_fuzz: true,
  enable_short_term: true,
  learning_steps: ['1m', '10m'],
  relearning_steps: ['1m'],
});

/** A ts-fsrs Card carrying its content key (used for deterministic fuzz). */
export type KeyedCard = Card & { card_key: string };

let scheduler: FSRS | null = null;

/** Shared scheduler instance. Pure/synchronous — safe to reuse everywhere. */
export function getScheduler(): FSRS {
  if (!scheduler) {
    scheduler = fsrs(FSRS_PARAMS);
    scheduler.useStrategy(StrategyMode.SEED, GenSeedStrategyWithCardId('card_key'));
  }
  return scheduler;
}

/** Fresh, never-reviewed card for a vocab item. */
export function newCard(key: string, now: Date): KeyedCard {
  const card = createEmptyCard(now) as KeyedCard;
  card.card_key = key;
  return card;
}
